import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { Platform } from 'react-native';
import { currentWebReturnTo } from '../utils/webReturnTo';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useAuth } from './AuthContext';
import { analytics } from '../services/analytics';
import { AuthCTAModal, AuthCTATrigger } from '../components/AuthCTAModal';
import { RootStackParamList } from '../navigation/types';
import type { User } from '../types/auth';
import { storage } from '../utils/storage';
import {
  SoftAuthSource,
  trackSoftAuthActionSelected,
  trackSoftAuthOpened,
} from '../services/homeAnalytics';
import { clearSignupFunnel, markSignupFunnel } from '../services/signupFunnel';
import type { SignupSource } from '../services/marketingTags';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

const PENDING_AUTH_CTA_KEY = 'agape_auth_cta_pending_action';

type AuthCTAMeta = {
  heading?: string;
  homeSource?: SoftAuthSource;
  returnTo?: string;
  /**
   * CORE-1277 — какой CTA привёл сюда, если у него есть своя цель воронки.
   *
   * Отдельно от `homeSource`: тот описывает словарь главной (CORE-595) и
   * включает действия без регистрационной воронки, а «+ Live-сессия» живёт в
   * шапке и в этот словарь не входит вовсе.
   */
  signupFunnel?: SignupSource;
};

interface AuthCTAContextType {
  showAuthCTA: (trigger: AuthCTATrigger, description?: string, onAuthenticated?: (user?: User) => void, meta?: AuthCTAMeta) => void;
}

const AuthCTAContext = createContext<AuthCTAContextType>({
  showAuthCTA: () => {},
});

export const useAuthCTA = () => useContext(AuthCTAContext);

export const AuthCTAProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigation = useNavigation<NavigationProp>();
  const { isAuthenticated, user } = useAuth();

  const [isVisible, setIsVisible] = useState(false);
  const [trigger, setTrigger] = useState<AuthCTATrigger>('gated_action');
  const [description, setDescription] = useState<string | null>(null);
  const [heading, setHeading] = useState<string | undefined>(undefined);
  const pendingActionRef = useRef<((user?: User) => void) | null>(null);
  const pendingHomeSourceRef = useRef<SoftAuthSource | null>(null);
  const pendingSignupFunnelRef = useRef<SignupSource | null>(null);
  const pendingReturnToRef = useRef<string | undefined>(undefined);
  const wasAuthenticatedRef = useRef(isAuthenticated);

  // CORE-1681 (QA 31.10.2026) — with the fragment: a master's discussion and the message to
  // highlight live in `#communityChat/…`, and `pathname + search` alone dropped them.
  const readReturnTo = useCallback(() => (Platform.OS === 'web' ? currentWebReturnTo() : undefined), []);

  // Execute pending action when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && !wasAuthenticatedRef.current && pendingActionRef.current) {
      const action = pendingActionRef.current;
      pendingActionRef.current = null;
      pendingHomeSourceRef.current = null;
      pendingSignupFunnelRef.current = null;
      pendingReturnToRef.current = undefined;
      storage.removeJSON(PENDING_AUTH_CTA_KEY).catch(() => {});
      // Small delay to let the navigation settle after login
      setTimeout(() => action(user ?? undefined), 300);
    }
    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated, user]);

  const showAuthCTA = useCallback((t: AuthCTATrigger, desc?: string, onAuthenticated?: (user?: User) => void, meta?: AuthCTAMeta) => {
    if (isAuthenticated) {
      onAuthenticated?.();
      return;
    }
    const returnTo = meta?.returnTo ?? readReturnTo();
    setTrigger(t);
    setDescription(desc ?? null);
    setHeading(meta?.heading);
    pendingActionRef.current = onAuthenticated ?? null;
    pendingHomeSourceRef.current = meta?.homeSource ?? null;
    pendingSignupFunnelRef.current = meta?.signupFunnel ?? null;
    pendingReturnToRef.current = returnTo;
    analytics.track('cta_shown', { trigger_source: t });
    if (meta?.homeSource) trackSoftAuthOpened(meta.homeSource);
    storage.setJSON(PENDING_AUTH_CTA_KEY, {
      trigger: t,
      description: desc ?? null,
      returnTo,
      homeSource: meta?.homeSource ?? null,
      createdAt: Date.now(),
    }).catch(() => {});
    setIsVisible(true);
  }, [isAuthenticated, readReturnTo]);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    pendingActionRef.current = null;
    const source = pendingHomeSourceRef.current;
    pendingHomeSourceRef.current = null;
    pendingSignupFunnelRef.current = null;
    pendingReturnToRef.current = undefined;
    if (source) trackSoftAuthActionSelected({ source, action: 'dismissed' });
    // Закрытое окно — это не начатая регистрация: метка воронки не должна
    // пережить отказ и достаться следующей, ни от какого CTA не пришедшей.
    void clearSignupFunnel();
    storage.removeJSON(PENDING_AUTH_CTA_KEY).catch(() => {});
    analytics.track('cta_dismissed', { trigger_source: trigger });
  }, [trigger]);

  const handleSignUp = useCallback(() => {
    setIsVisible(false);
    const source = pendingHomeSourceRef.current;
    if (source) trackSoftAuthActionSelected({ source, action: 'register' });
    // CORE-1277 — воронка начинается ровно здесь: CTA нажат И выбрана
    // регистрация. До этого места человек ещё ничего не начинал.
    const funnel = pendingSignupFunnelRef.current;
    if (funnel) void markSignupFunnel(funnel);
    else void clearSignupFunnel();
    analytics.track('cta_signup_clicked', { trigger_source: trigger });
    navigation.navigate('Register', { returnTo: pendingReturnToRef.current ?? readReturnTo() });
  }, [navigation, readReturnTo, trigger]);

  const handleLogIn = useCallback(() => {
    setIsVisible(false);
    const source = pendingHomeSourceRef.current;
    if (source) trackSoftAuthActionSelected({ source, action: 'login' });
    // Вход в существующий аккаунт — не регистрация: метка снимается.
    void clearSignupFunnel();
    analytics.track('cta_login_clicked', { trigger_source: trigger });
    navigation.navigate('Login', { returnTo: pendingReturnToRef.current ?? readReturnTo() });
  }, [navigation, readReturnTo, trigger]);

  return (
    <AuthCTAContext.Provider value={{ showAuthCTA }}>
      {children}
      {isVisible && (
        <AuthCTAModal
          headingOverride={heading}
          appearance={pendingHomeSourceRef.current === 'light_path' ? 'rhythm' : undefined}
          trigger={trigger}
          description={description}
          onSignUp={handleSignUp}
          onLogIn={handleLogIn}
          onDismiss={handleDismiss}
        />
      )}
    </AuthCTAContext.Provider>
  );
};
